/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/indent */
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FC, ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import checkDevice from "../../utils/isTouch";
import Animation, { AnimationProps } from "./Animation";
import Button from "./Button";
import IconBtn from "./IconBtn";

export interface PortalProps {
  onClose?: () => void;
  show: boolean;
  title?: ReactNode;
  position: "side" | "center" | "top";
  // fireEvent?: () => void;
  buttonElement?: ReactNode;
  children?: ReactNode;
  modalWidth?: string;
  modalHeight?: string;
  modalWidthTouch?: string;
  hasHeader?: false;
  mini?: boolean;
  calcHeight?: boolean;
  /**
   * Size the box to its content instead of forcing the body to a percentage of the modal.
   * The default `height: calc(100% - 4rem)` resolves against the inset-0 containing block,
   * not the content, so a short modal still renders full-height with blank space below it.
   * With this on, the body grows with its content and only scrolls past the max-height cap.
   */
  fitContent?: boolean;
  /**
   * Id of the element that names the dialog, for a modal that renders its own heading instead of
   * passing `title`. Without it a screen reader announces "dialog" and nothing else.
   */
  ariaLabelledBy?: string;
}

export type Keys = {
  [key: string]: () => void;
};

export const Modal: FC<PortalProps> = ({
  children,
  show,
  onClose,
  title,
  position,
  buttonElement,
  modalWidth,
  modalHeight,
  modalWidthTouch = "100%",
  hasHeader = true,
  mini,
  calcHeight = true,
  fitContent = false,
  ariaLabelledBy
}) => {
  const isTouch = checkDevice();
  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      const keys: Keys = {
        Escape: () => {
          e.preventDefault();
          window.removeEventListener("keyup", handleKeyUp, false);
          onClose && onClose();
        }
      };
      if (keys[e.key]) {
        keys[e.key]();
      }
    };

    window.addEventListener("keyup", handleKeyUp, false);
    show && document.body.classList.add("overflow-hidden");

    return () => {
      window.removeEventListener("keyup", handleKeyUp, false);
      show && document.body.classList.remove("overflow-hidden");
    };
  }, [show]);

  // let modalPosition;
  let modalPosition =
    "md:w-80 w-full h-full right-0 top-0 bottom-0 left-0 md:left-auto md:bottom:auto";
  let animationType: AnimationProps["type"] = "fade-right";

  if (position === "center") {
    // fitContent centres with a transform instead of `inset-0 m-auto`. With top and bottom
    // both pinned and height auto, CSS solves the height to the full containing block
    // (CSS 2.1 §10.6.4 rule 5), so max-h just clamps it to a constant 550px and short
    // content leaves a blank gap. Pinning only the midpoint leaves height genuinely auto.
    modalPosition = mini
      ? "max-h-[180px] max-w-[350px] m-auto inset-0 rounded-lg"
      : fitContent
        ? "h-auto max-h-[550px] md:w-[500px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg"
        : "md:min-h-fit md:max-h-[550px] md:w-[500px] m-auto inset-0 rounded-lg";
    animationType = "fade-out";
  } else if (position === "top") {
    modalPosition = "m-auto w-max top-32 left-0 right-0 rounded-lg";
    animationType = "fade-in";
  }

  // if (position === "side")
  //   modalPosition =
  //     "md:w-80 w-full h-full right-0 top-0 bottom-0 left-0 md:left-auto md:bottom:auto";
  // else if (position === "center") {
  //   modalPosition =
  //     "md:w-[500px] md:min-h-[250px] md:max-h-[550px] md:m-auto inset-0 rounded-lg";
  // }
  return show
    ? createPortal(
      <div className="fixed inset-0 z-50">
        <Animation show={show} type="fade" duration={300}>
          <span
            onClick={onClose}
            className={"absolute bg-black bg-opacity-20 inset-0 z-40"}
          />
        </Animation>
        <Animation
          show={show}
          type={animationType}
          duration={300}
          translateShift={{ out: "translate-x-80" }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            style={{
              width: !isTouch ? modalWidth : modalWidthTouch, height: modalHeight
            }}
            className={`absolute bg-white overflow-hidden z-50 ${modalPosition}`}
          >
            {hasHeader ? (
              <div className="flex shadow items-center px-4 py-1 justify-between h-14">
                <div>{title}</div>
                <IconBtn onClick={onClose} ariaLabel="Mbyll">
                  <XMarkIcon className="w-5 h-5" />
                </IconBtn>
              </div>
            ) : null}
            <div
              className="overflow-y-auto overflow-x-hidden"
              style={
                fitContent
                  ? {
                    // Cap only. Height stays auto so the body hugs its content, and the
                    // 3.5rem subtracted is the h-14 header so the cap covers the whole box.
                    maxHeight: hasHeader ? "calc(550px - 3.5rem)" : "550px"
                  }
                  : {
                    minHeight: `${calcHeight && buttonElement
                        ? "calc(100% - 7rem)"
                        : "calc(100% - 4rem)"
                      }`,
                    height: `${calcHeight && buttonElement
                        ? "calc(100% - 7rem)"
                        : "calc(100% - 4rem)"
                      }`
                  }
              }
            >
              {children}
            </div>
            {buttonElement && (
              <div
                className={`w-full  flex items-center ${position === "side"
                    ? "shadow-lg shadow-black-900 justify-center h-16"
                    : `${mini
                      ? "justify-center px-6 w-full"
                      : "justify-end px-4"
                    } h-16`
                  }`}
              >
                {position === "center" && (
                  <Button
                    btnType="custom"
                    className={`mr-4 border ${mini ? "w-auto" : ""}`}
                    onClick={onClose}
                  >
                    Anulo
                  </Button>
                )}
                {buttonElement}
              </div>
            )}
          </div>
        </Animation>
      </div>,
      document.body
    )
    : null;
};

export default Modal;
